<script>
import ModalStudentAccountSubscriptionComponent from '../features/ModalStudentAccountSubscriptionComponent';

export default {
  name: 'ModalStudentAccountSubscriptionView',
  extends: ModalStudentAccountSubscriptionComponent,
  computed: {
    selectedPlan() {
      return this.subscriptionPlans?.find((p) => p.id === this.planSelected) || null;
    },
    canAdvanceStep() {
      return this.subscriptionValidation(this.currentStep);
    },
  },
  methods: {
    selectPlan(planId) {
      this.planSelected = planId;
    },
  },
};
</script>

<template>
  <el-dialog
    v-model="conf.isVisible"
    class="subscription-account-modal student-subscription-wizard-modal"
    width="860px"
    append-to-body
    :before-close="onCancel"
    :close-on-click-modal="false"
  >
    <!-- Cabecera personalizada del modal (Estándar Appmisionate) -->
    <template #header>
      <div class="student_modal_header">
        <div class="student_modal_header_icon">
          <i class="fas fa-crown" />
        </div>
        <div class="student_modal_header_info">
          <h3 class="student_modal_title">
            Suscripción y Membresía
          </h3>
          <p class="student_modal_subtitle">
            Elige tu plan de estudio, aplica tu forma de pago y desbloquea acceso completo a la plataforma
          </p>
        </div>
      </div>
    </template>

    <div
      v-if="formData"
      v-loading="isLoading"
      class="subscription_wizard_container"
    >
      <!-- Stepper / Barra de Progreso del Wizard -->
      <div class="wizard_stepper_track">
        <!-- Paso 0: Planes -->
        <div
          class="stepper_item"
          :class="{ 'is_active': isStepActive(0), 'is_done': isStepDone(0) }"
          @click="goToStep(0)"
        >
          <div class="stepper_circle">
            <i
              v-if="isStepDone(0)"
              class="fas fa-check"
            />
            <span v-else>1</span>
          </div>
          <div class="stepper_texts">
            <span class="stepper_title">Planes</span>
            <span class="stepper_subtitle">Elige tu membresía</span>
          </div>
        </div>

        <div
          class="stepper_connector"
          :class="{ 'is_done': isStepDone(0) }"
        />

        <!-- Paso 1: Pago -->
        <div
          class="stepper_item"
          :class="{ 'is_active': isStepActive(1), 'is_done': isStepDone(1) }"
          @click="goToStep(1)"
        >
          <div class="stepper_circle">
            <i
              v-if="isStepDone(1)"
              class="fas fa-check"
            />
            <span v-else>2</span>
          </div>
          <div class="stepper_texts">
            <span class="stepper_title">Pago</span>
            <span class="stepper_subtitle">Código / Descuento</span>
          </div>
        </div>

        <div
          class="stepper_connector"
          :class="{ 'is_done': isStepDone(1) }"
        />

        <!-- Paso 2: Resumen -->
        <div
          class="stepper_item"
          :class="{ 'is_active': isStepActive(2), 'is_done': isStepDone(2) }"
          @click="goToStep(2)"
        >
          <div class="stepper_circle">
            <i
              v-if="isStepDone(2)"
              class="fas fa-check"
            />
            <span v-else>3</span>
          </div>
          <div class="stepper_texts">
            <span class="stepper_title">Confirmación</span>
            <span class="stepper_subtitle">Resumen y activación</span>
          </div>
        </div>
      </div>

      <!-- Contenido del Paso 0: PLANES -->
      <div
        v-if="isStepActive(0)"
        class="student_form_section"
      >
        <div class="student_section_header">
          <h4 class="student_section_title">
            <i class="fas fa-layer-group" /> Planes de Estudio Disponibles
          </h4>
        </div>

        <div
          v-if="subscriptionPlans && subscriptionPlans.length > 0"
          class="plan_selection_grid"
        >
          <div
            v-for="plan in subscriptionPlans"
            :key="plan.id"
            class="subscription_plan_card"
            :class="{ 'is_selected': planSelected === plan.id }"
            @click="selectPlan(plan.id)"
          >
            <div class="plan_card_header">
              <div class="plan_header_left">
                <el-radio
                  v-model="planSelected"
                  :value="plan.id"
                  class="mr-0"
                />
                <div class="plan_icon_box">
                  <i class="fas fa-graduation-cap" />
                </div>
                <h5 class="plan_name">
                  {{ plan.name }}
                </h5>
              </div>

              <div class="plan_price_badge">
                <span class="price_amount">{{ formatMoney(plan.amount) }}</span>
                <span class="price_period">/ plan</span>
              </div>
            </div>

            <p class="plan_card_desc">
              {{ plan.description }}
            </p>

            <div class="plan_features_list">
              <span class="plan_feature_chip">
                <i class="fas fa-check" /> Simuladores de prueba ilimitados
              </span>
              <span class="plan_feature_chip">
                <i class="fas fa-check" /> Banco de preguntas oficial
              </span>
              <span class="plan_feature_chip">
                <i class="fas fa-check" /> Evaluaciones y estadísticas
              </span>
            </div>
          </div>
        </div>

        <div
          v-else
          class="p-4 text-center text-muted"
        >
          <i class="fas fa-spinner fa-spin mr-2" /> Cargando planes de suscripción...
        </div>
      </div>

      <!-- Contenido del Paso 1: PAGO -->
      <div
        v-if="isStepActive(1)"
        class="student_form_section"
      >
        <div class="student_section_header">
          <h4 class="student_section_title">
            <i class="fas fa-credit-card" /> Forma de Pago y Descuento
          </h4>
        </div>

        <!-- Resumen del plan seleccionado -->
        <div class="selected_plan_summary_banner">
          <div class="summary_plan_info">
            <i class="fas fa-check-circle" />
            <div>
              <span class="plan_chosen_name">{{ getSubscriptionPlanMethodName() }}</span>
              <span
                v-if="selectedPlan"
                class="plan_chosen_price ml-2"
              >({{ formatMoney(selectedPlan.amount) }})</span>
            </div>
          </div>
          <button
            type="button"
            class="btn_change_plan"
            @click="goToStep(0)"
          >
            Cambiar Plan
          </button>
        </div>

        <!-- Método de Pago: Código -->
        <div class="payment_method_card">
          <div class="payment_icon">
            <i class="fas fa-ticket-alt" />
          </div>
          <div class="payment_texts">
            <h5 class="payment_title">
              Código de Descuento o Beca
            </h5>
            <p class="payment_desc">
              Ingresa el código promocional o cupón asignado para canjear y pagar tu inscripción
            </p>
          </div>
        </div>

        <div class="discount_code_input_wrap">
          <el-input
            v-model="discountCode"
            size="large"
            placeholder="Ingresa tu Código de Descuento (ej. PROMO2026)"
            clearable
          >
            <template #prefix>
              <i class="fas fa-tag" />
            </template>
          </el-input>

          <span
            v-if="hasDiscountCode()"
            class="discount_code_status is_valid"
          >
            <i class="fas fa-check-circle" /> Código listo para ser aplicado
          </span>
          <span
            v-else
            class="discount_code_status is_required"
          >
            <i class="fas fa-info-circle" /> Escribe tu código para habilitar el siguiente paso
          </span>
        </div>
      </div>

      <!-- Contenido del Paso 2: RESUMEN -->
      <div
        v-if="isStepActive(2)"
        class="student_form_section"
      >
        <div class="student_section_header">
          <h4 class="student_section_title">
            <i class="fas fa-receipt" /> Resumen de la Suscripción
          </h4>
        </div>

        <!-- Estado previo al pago -->
        <div
          v-if="!subscriptionDetails"
          class="checkout_summary_card"
        >
          <div class="summary_card_header">
            <i class="fas fa-file-invoice" /> Desglose de Activación
          </div>

          <div class="summary_row">
            <span class="row_label">Plan de Suscripción:</span>
            <span class="row_val">{{ getSubscriptionPlanMethodName() }}</span>
          </div>

          <div class="summary_row">
            <span class="row_label">Método de Pago:</span>
            <span class="row_val">{{ getPaymentMethodName() }}</span>
          </div>

          <div
            v-if="discountCode"
            class="summary_row"
          >
            <span class="row_label">Código de Descuento:</span>
            <span class="code_badge">{{ discountCode }}</span>
          </div>

          <div
            v-if="selectedPlan"
            class="summary_row is_total"
          >
            <span class="row_label">Monto del Plan:</span>
            <span class="row_val">{{ formatMoney(selectedPlan.amount) }}</span>
          </div>

          <div class="checkout_notice">
            <i class="fas fa-shield-alt" />
            <span>Al hacer clic en <strong>"Pagar y Activar"</strong>, se validará tu código de descuento en el servidor y se activará inmediatamente el acceso a todos los contenidos de tu plan.</span>
          </div>
        </div>

        <!-- Estado posterior al pago exitoso -->
        <div
          v-else
          class="subscription_success_card"
        >
          <div class="success_icon_bounce">
            <i class="fas fa-check-circle" />
          </div>
          <h4 class="success_title">
            ¡Suscripción Activada Exitosamente!
          </h4>
          <p class="success_subtitle">
            Tu cuenta ahora cuenta con acceso completo a los cursos, simuladores y recursos de la plataforma.
          </p>

          <div class="subscription_date_box">
            <i class="fas fa-calendar-check" />
            <span>Vigencia: <strong>{{ formatDate(subscriptionDetails.subscriptionDate) }}</strong> al <strong>{{ formatDate(subscriptionDetails.subscriptionEndDate) }}</strong></span>
          </div>
        </div>
      </div>
    </div>

    <!-- Footer del modal con navegación de wizard -->
    <template #footer>
      <div class="student_modal_footer">
        <span
          v-if="onHideBackButton()"
          class="footer_note"
        >
          <i class="fas fa-info-circle mr-1" />
          Paso {{ currentStep + 1 }} de {{ totalSteps }}
        </span>
        <span
          v-else
          class="footer_note"
        >
          <i class="fas fa-check-circle mr-1 text-success" />
          Membresía activa en el sistema
        </span>

        <div class="footer_actions">
          <el-button
            v-if="onHideBackButton() && currentStep > 0"
            class="btn_cancel_student_modal"
            @click="onPrevStep"
          >
            <i class="fas fa-arrow-left mr-1" /> Anterior
          </el-button>

          <el-button
            v-if="onHideBackButton() && currentStep < totalSteps - 1"
            type="primary"
            class="btn_submit_student_info"
            :disabled="!canAdvanceStep"
            @click="onNextStep"
          >
            Siguiente <i class="fas fa-arrow-right ml-1" />
          </el-button>

          <el-button
            v-if="onHideBackButton() && currentStep === totalSteps - 1"
            type="primary"
            class="btn_submit_student_info"
            :loading="isLoading"
            @click="onPaySubscription"
          >
            <i class="fas fa-lock mr-2" /> Pagar y Activar
          </el-button>

          <el-button
            v-if="onShowFinishButton()"
            type="success"
            class="btn_submit_student_info"
            @click="onSubscriptionFinish"
          >
            <i class="fas fa-rocket mr-2" /> Finalizar y Comenzar
          </el-button>
        </div>
      </div>
    </template>
  </el-dialog>
</template>
